import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Backdrop,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import { openSnackbar } from "api/snackbar";
import { SnackbarProps } from "types/snackbar";
import { Formik, Form, Field } from "formik";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Article } from "../../../../../types/article";
import { Comment } from "../../../../../types/comment";

import { mockComment } from "./mockData";

const AddEditComment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [commentData, setCommentData] = useState<Comment>(mockComment);

  useEffect(() => {
    if (id) {
      fetchComment();
    }
  }, [id]);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoadingArticles(true);
    try {
      const response = await USERAXIOS.get("/blogs?page=1");
      setArticles(response.data.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
      openSnackbar({
        open: true,
        message: "Error fetching articles",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoadingArticles(false);
    }
  };

  const fetchComment = async () => {
    try {
      setLoading(true);
      const response = await USERAXIOS.get(`/blog_comments/${id}`);
      const { status, blog_id, user_id, title, text } = response.data.data;
      setCommentData({
        status: status ? 1 : 0,
        blog_id,
        user_id,
        title,
        text,
      });
    } catch (error) {
      console.error("Error fetching comment:", error);
      openSnackbar({
        open: true,
        message: "Error fetching comment",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    values: Comment,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const formData = new FormData();
      formData.append("user_id", values.user_id.toString());
      formData.append("status", values.status.toString());
      formData.append("blog_id", values.blog_id.toString());
      formData.append("title", values.title);
      formData.append("text", values.text);

      if (id) {
        await USERAXIOS.post(`/blog_comments/${id}`, formData);
      } else {
        await USERAXIOS.post("/blog_comments", formData);
      }

      openSnackbar({
        open: true,
        message: `Comment ${id ? "updated" : "created"} successfully`,
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);

      navigate("/comments-list");
    } catch (error) {
      console.error("Error saving comment:", error);
      openSnackbar({
        open: true,
        message: `Error ${id ? "updating" : "creating"} comment`,
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "primary",
        }}
        open={loading}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
        </Stack>
      </Backdrop>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              <Typography variant="h4" color="primary">
                <FormattedMessage id={id ? "Edit Comment" : "Add Comment"} />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Formik
                initialValues={commentData}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ isSubmitting }) => (
                  <Form>
                    <Divider style={{ marginBottom: "40px" }} />

                    <Stack spacing={3}>
                      <Typography>
                        <FormattedMessage id="Title" />{" "}
                      </Typography>
                      <Field name="title">
                        {({ field }: { field: any }) => (
                          <TextField {...field} fullWidth required />
                        )}
                      </Field>

                      <Stack spacing={1}>
                        <InputLabel htmlFor="text">
                          <FormattedMessage id="Content" />
                        </InputLabel>
                        <Field name="text">
                          {({ field }: { field: any }) => (
                            <ReactQuill
                              style={{ height: "150px", marginBottom: "50px" }}
                              theme="snow"
                              value={field.value}
                              onChange={(content) =>
                                field.onChange({
                                  target: { name: "text", value: content },
                                })
                              }
                            />
                          )}
                        </Field>
                      </Stack>

                      <FormControl fullWidth>
                        <Field name="blog_id">
                          {({ field }: { field: any }) => (
                            <Select
                              {...field}
                              displayEmpty
                              renderValue={(selected) => {
                                if (!selected) {
                                  return (
                                    <FormattedMessage id="Select Article" />
                                  );
                                }
                                return articles.find(
                                  (article) => article.id === selected
                                )?.title;
                              }}
                            >
                              {loadingArticles ? (
                                <MenuItem disabled>
                                  <CircularProgress size={20} />
                                </MenuItem>
                              ) : (
                                articles.map((article) => (
                                  <MenuItem key={article.id} value={article.id}>
                                    {article.title}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          )}
                        </Field>
                      </FormControl>

                      <Field name="status">
                        {({ field }: { field: any }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value === 1}
                                onChange={(e) =>
                                  field.onChange({
                                    target: {
                                      name: "status",
                                      value: e.target.checked ? 1 : 0,
                                    },
                                  })
                                }
                              />
                            }
                            label="status"

                            // label={field.value === 1 ? "1" : "0"}
                          />
                        )}
                      </Field>

                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        marginTop="2rem"
                      >
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                            <Grid item md={2} />
                            <Grid item md={4}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate("/comments-list")}
                              >
                                <FormattedMessage id="Cancel" />
                              </Button>
                            </Grid>
                            <Grid item md={4}>
                              <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <CircularProgress
                                    size="1.5rem"
                                    style={{ color: "white" }}
                                  />
                                ) : (
                                  <FormattedMessage
                                    id={id ? "Update" : "Create"}
                                  />
                                )}
                              </Button>
                            </Grid>
                            <Grid item md={2} />
                          </Stack>
                        </Grid>
                      </Stack>
                    </Stack>
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddEditComment;
